import { createVitals } from './effects.js';
import { forfeit } from './gold.js';
import { remove } from './inventory.js';
import { DEFAULT_MOVE_SPEED, moveSpeed } from './movement.js';
import { TICK_RATE_HZ } from './tick.js';
import { spawn } from './world.js';
/**
 * What a player's death costs, and how a character is put on a floor.
 *
 * ## Why this is not `effects.ts`
 *
 * `effects.ts` owns death as a **state on a life pool**: the pool empties, the
 * crossing is reported once as `died`, and nothing is removed from anywhere —
 * *"the death policy reads `died` off a `VitalsChange` and calls `despawn`
 * itself"*. That is exactly right for a monster, whose whole death is a removal
 * (`floor-tick.ts` reaps it and the wire stops mentioning it).
 *
 * A player is not a body, though. A player is a **seat** with a body attached:
 * the connection outlives the corpse, the party is still fighting, and the run
 * has to be *ended* rather than merely stopped — with a bill attached. None of
 * that is a fact about a life pool, and `effects.ts` deliberately knows nothing
 * about ids, positions or floors, so it cannot state any of it. This module is
 * the other half — the toll, the pause before the run ends, and `embody`, the
 * one way a character is put on a floor at all.
 *
 * ## What a death costs (`docs/GAME-DESIGN.md`, `TODO.md` #3, #300, #428)
 *
 * *"Default: run ends, equipped gear is kept, loot picked up this run and some
 * gold are lost. Creates the tension around when to portal out."*
 *
 * That was decided on 2026-08-11 and stood unimplemented until both halves of
 * it existed — loot (M4) and a town to be sent back to (M6). The interim shape,
 * a three-second wait and a walk back from the entrance, is gone: **nothing
 * respawns**. A death now charges a **toll** and the run ends, and `recordDeath`
 * is where the toll is charged. `TODO.md` #300 named that call for exactly this
 * — *"`recordDeath` is where a policy would be chosen"* — and one call is what
 * keeps the two facts about a death together, so nothing can record one without
 * charging it or charge one twice.
 *
 * **Only the default mode exists.** Corpse run is a later setting behind this
 * same call (Ronald, 2026-08-13) and neither it nor the setting that would
 * select it is built here. Guessing at the shape of a policy argument before
 * there is a second policy is the config DSL `CLAUDE.md` forbids.
 *
 * ### What the toll can reach, and what it therefore cannot take
 *
 * A `Carried` is **the packing grid and the purse, and nothing else**. That is
 * the whole enforcement of *"equipped gear is kept"*: the equipment list, the
 * belt and the stash are not arguments, so no rule here can take from them
 * however an item got there. An item found this run and then **worn** is
 * therefore kept — the guarantee about equipment is unconditional, and the one
 * that is qualified is the one about loot *in the bag*. It is also the reading
 * that makes a death a decision rather than a disaster: put the upgrade on and
 * it is yours, leave it in the bag and it is what you are gambling by going one
 * floor deeper.
 *
 * ### "Picked up this run" is a caller's fact, not core's
 *
 * Core has no session, no floor and no seat, so it cannot know which of the
 * things in a grid were carried in from town. The caller says, by id — a room
 * knows, because it loaded a snapshot when the seat was taken and every pickup
 * since is its own doing. A mark that names something no longer in the grid
 * takes nothing: the grid is the truth about what is there, and the mark is only
 * a claim about where it came from.
 *
 * ## Everything here is counted in ticks, and reads no clock
 *
 * A wait measured in milliseconds is a wait that cannot be tested, cannot be
 * replayed, and does not survive a stalled frame: `accumulateSteps` can buy five
 * whole steps at once, and a timer that asked the clock how long it had been
 * would count that stall as one tick's worth of waiting on the server and five
 * on a client that did not stall. So a death is *when it happened* and *when the
 * run ends*, both in fixed 20 Hz steps, and `runOver` is a comparison of two
 * integers — the same shape, and for the same reason, as `readyToSwing`
 * (`attack.ts`).
 *
 * ## What this module does *not* decide
 *
 * **Where the entrance is.** `dungeon.ts` computes it and the room holds it;
 * this places a character on whatever cell it is handed, through the guarded
 * `spawn`, so the "nothing is stuck" invariant holds for a character arriving
 * down the stairs exactly as it does for a joining one. **Who is dead** is
 * `effects.ts`'s `isDead`, and **when a body leaves the world** is the tick order's
 * (`floor-tick.ts`). And nothing here holds a *collection* of deaths: like
 * `Vitals`, a `Death` is one thing about one player, and the map from a seat to
 * it belongs to whatever owns seats.
 */
/**
 * The pause between dying and the run ending, as the design states it: *"a short
 * fixed delay (~3 s)"*.
 *
 * Named in seconds as well as in ticks because the design's number is in
 * seconds and the simulation's is in steps. Deriving one from the other means a
 * change to the tick rate cannot silently change how long a player lies there.
 */
export const RUN_END_DELAY_SECONDS = 3;
/**
 * The same pause in fixed simulation steps — sixty of them.
 *
 * Long enough to register the death and see what killed you, short enough that
 * it is not a punishment on its own. It is **not** a countdown to standing back
 * up: the interim shape that ended in a character at the entrance is gone, and
 * what these sixty ticks end in is the seat leaving and the player being back in
 * town (`docs/GAME-DESIGN.md`, "Death and the end of a run").
 *
 * It is also, deliberately, far longer than `MAX_CATCHUP_STEPS`. One frame can
 * buy at most five steps, so **no seat can die and have its run end inside one
 * frame** — which is what lets a room apply the two events in whatever order it
 * finds them without them ever being the same seat's.
 */
export const RUN_END_DELAY_TICKS = RUN_END_DELAY_SECONDS * TICK_RATE_HZ;
const assertTick = (what, value, low) => {
    // Phrased as a negated comparison so NaN — a tick counter that was never a
    // number — is rejected along with the out-of-range values.
    if (!(Number.isSafeInteger(value) && value >= low)) {
        throw new RangeError(`${what} must be a whole number of at least ${low}, received ${value}`);
    }
};
/**
 * What fraction of a purse a death takes, as a whole percentage.
 *
 * **A placeholder in the sense `MONSTER_EXPERIENCE_COEFFICIENT` is one**: the
 * design says *"some gold"* and nothing more, D2's own answer is a proportion of
 * what you are carrying rather than a flat fee, and nobody has played this
 * number. What is *not* a placeholder is the shape — a percentage is the only
 * form that makes the penalty scale with what a player has at risk, which is the
 * whole of what *"creates the tension around when to portal out"* asks for. A
 * flat fee would be everything to a fresh character and nothing to a deep one.
 *
 * ## What a quarter costs, in floors
 *
 * `gold.ts` measures a floor at roughly `540 + 270 × depth` gold — 810 on floor
 * 1, 3 240 on floor 10 — and caps a purse at `level × 10 000`, which it works
 * out as about twelve floors of hoarding at depth 1 and never more than
 * thirty-seven however deep. Against that:
 *
 *   - a player who banks every floor loses **a quarter of one floor's takings**
 *     — about 200 gold on floor 1, about 800 on floor 10;
 *   - a player who has not been to town in ten floors loses **two and a half
 *     floors' worth**;
 *   - a full purse loses **three floors' worth at depth 1 and about nine** at
 *     the deepest the cap ever represents.
 *
 * That is the point of the shape rather than a side effect of it: the cost of
 * dying is a function of how long since you last went back, so *"one more
 * floor"* is a real question with a real price on it. It is deliberately
 * recoverable — one careless death is a floor or two of gold, not a character —
 * because the loot half of this penalty is already the sharp end, and two
 * disasters at once is how a game teaches people not to descend at all.
 *
 * Twenty-five and not ten, which is inside the variance of a single floor and
 * would be felt as nothing; and not fifty, which makes carrying gold irrational
 * and turns the town trip into a chore between every floor — the exact tedium
 * the carry cap is already braking.
 */
export const DEATH_GOLD_FORFEIT_PERCENT = 25;
/** What this purse owes a death: a quarter of it, in whole gold. */
export const deathGoldForfeit = (purse) => Math.floor((purse.carried * DEATH_GOLD_FORFEIT_PERCENT) / 100);
const NOTHING = { items: [], gold: 0 };
/**
 * Starts the clock on a death, and charges it.
 *
 * The tick is handed in rather than read, because there is nothing here to read
 * it from — that is the whole point of a pause counted in steps.
 *
 * A pause of no ticks at all is refused, and it is the one bound worth stating:
 * a run that ends on the tick the body fell is a death that nothing ever
 * observes — no client sees the corpse, no party member sees them drop, and the
 * design's "long enough to register the death" is nothing at all. Together with
 * a fractional tick and a clock that is not a number, that is malformed
 * construction rather than an ordinary outcome, and it throws like every other
 * one in core. It throws **before** anything is charged, so a caller that was
 * handed a bad tick is not also handed a half-emptied bag.
 *
 * Both halves of the toll are `gold.ts`'s and `inventory.ts`'s own operations
 * rather than arithmetic written here: `forfeit` is *"the death penalty's half"*
 * of the purse and cannot refuse, and `remove` is what taking a thing out of a
 * grid means. The survivors keep the cells they were in — `remove` filters and
 * never repacks — because a penalty that also rearranged the bag would be a
 * second, invisible punishment.
 */
export const recordDeath = ({ tick, carried, found, delayTicks = RUN_END_DELAY_TICKS, }) => {
    assertTick('The tick a body died on', tick, 0);
    assertTick('A run-end delay', delayTicks, 1);
    const death = { diedAtTick: tick, endsAtTick: tick + delayTicks };
    if (carried === undefined)
        return { death, kept: null, lost: NOTHING };
    const items = [];
    let inventory = carried.inventory;
    // Walked over the grid rather than over the marks, and in the grid's own
    // order: what the character is actually holding is the truth, a mark that
    // names something no longer there is stale, and two machines charging the
    // same death must produce the same list in the same order.
    for (const placed of carried.inventory.items) {
        if (found?.has(placed.item.id) !== true)
            continue;
        const taken = remove(inventory, placed.item.id);
        // Unreachable: the id came out of this very grid one line above. Kept
        // because that is a property of the loop rather than of the type.
        if (!taken.ok)
            continue;
        inventory = taken.inventory;
        items.push(taken.removed.item);
    }
    const purse = forfeit(carried.purse, deathGoldForfeit(carried.purse));
    return { death, kept: { inventory, purse: purse.purse }, lost: { items, gold: purse.lost } };
};
/**
 * Whether this death's pause is over on `tick` — whether the run has ended.
 *
 * At or past the due tick, never only *on* it: a run of catch-up steps steps
 * through every tick in order, but a room that was handed a tick counter from
 * the wire or from a test may not land on the exact one, and a pause that fired
 * only on equality would leave that seat waiting forever.
 */
export const runOver = (death, tick) => tick >= death.endsAtTick;
/**
 * Puts a character on a floor: a body, and the life that goes with it.
 *
 * The pairing is the point. `world.ts` owns where a body is and `effects.ts`
 * owns what is happening to it, and the two are deliberately independent — so
 * *somebody* has to create them together, and every call site that did it by
 * hand was a chance for a body with no life (which fails closed everywhere and
 * therefore cannot act, silently) or a life with no body. Stated once, it is
 * also one place to state what a character arrives as:
 *
 *   - **Whole**, at full life with nothing running on it. Nothing carries onto a
 *     body this makes: no poison, no burning, no half-spent pool. A caller that
 *     wants otherwise — a descent, which is one character walking downstairs
 *     rather than a new one — writes over the top of it and says why.
 *   - **A character**, so poison may soften it and never land the killing blow
 *     (`POISON_LIFE_FLOOR`). That is `createVitals`'s default and D2's own rule;
 *     monsters say otherwise for themselves.
 *   - **Standing still, at the speed its gear gives it.** `spawn` gives no
 *     walker, so a character arrives with nowhere to be, and the first step is
 *     the player's to order. No *base* speed is offered here on purpose: a
 *     character arriving down the stairs walks at exactly the rate a joining one
 *     does, off the same base and the same modifiers, which is the day
 *     `TODO.md` #111 was waiting for. Faster
 *     Run/Walk reaches a character through the identical call a monster's Extra
 *     Fast reaches it through (`movement.ts`'s `moveSpeed`) — one mechanism, so
 *     the two halves of the game cannot drift apart on what a percent means.
 *   - **Somewhere it fits.** The guarded placement, unchanged: a cell whose
 *     clearance does not admit the body is refused rather than nudged, and the
 *     caller decides what that means. Nothing is said about a cell another body
 *     is standing on — bodies share ground, so a monster camped on the entrance
 *     does not refuse an arrival; it is stood on, and the overlap unwinds as
 *     either of them walks (`world.ts`).
 *
 * A join and a descent are the same act and go through the same call, so the
 * two can never drift apart on what a character is when it appears.
 */
export const embody = ({ world, id, cell, mover, maximumLife, modifiers = [] }) => {
    // Before the placement, so a malformed pool is not reported as a world that
    // grew a body it has no life for.
    const vitals = createVitals({ maximum: maximumLife });
    const placed = spawn(world, {
        id,
        kind: 'player',
        cell,
        mover,
        speed: moveSpeed(DEFAULT_MOVE_SPEED, modifiers),
    });
    if (!placed.ok)
        return { ok: false, reason: placed.reason };
    return { ok: true, world: placed.world, entity: placed.entity, vitals };
};
//# sourceMappingURL=death.js.map